import { Theme, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, DialogContentText, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { numberToUSDCurrency } from '../helpers/numberToUSDCurrency';
import { ApproveSettlement } from '../api/paymentsApi/settlementsApi';
import useSnackBar from '../hooks/useSnackBar';
import { AxiosError } from 'axios';
import { ErrorData } from '../types';
import { useState } from 'react';

const useStyles = makeStyles()((theme: Theme) => ({
    dialogContainer: {
        '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: '802px',
            boxSizing: 'border-box',
            padding: '32px',
            gap: '32px',
            border: `1px solid ${theme.palette.secondary.dark}`,
            borderRadius: '16px',
            backgroundColor: theme.palette.primary.light,
        },

        '& .MuiDialogContentText-root': {
            color: theme.palette.secondary.main,
        },
    },

    dialogTitle: {
        fontFamily: 'Poppins',
        fontWeight: 600,
        fontSize: '34px',
        lineHeight: '40.8px',
        padding: '0px',
        color: theme.palette.common.white,
    },

    dialogContentText: {
        fontFamily: 'Poppins',
        fontWeight: '600',
        fontSize: '20px',
        lineHeight: '24px',
    },

    dialogContentValue: {
        fontFamily: 'Poppins',
        fontWeight: '600',
        fontSize: '20px',
        lineHeight: '24px',
        color: theme.palette.common.white,
    },

    dialogActions: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',

        '@media (max-width: 550px)': {
            flexDirection: 'column',
            gap: '16px',

            '@media (max-width: 550px)': {
                paddingRight: '16px',
            },
        },
    },

    defaultButtonStyle: {
        height: '48px',
        maxWidth: '416px',
        width: '100%',
        borderRadius: '12px',
        fontFamily: 'Poppins',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '19.2px',
        textTransform: 'none',
        color: theme.palette.common.white,

        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.08)',
            background: 'rgba(255, 255, 255, 0.08)',
        },

        '@media (max-width: 550px)': {
            maxWidth: '100%',
        },
    },

    submitButton: {
        background: theme.palette.info.main,

        '&:hover': {
            background: theme.palette.info.main,
        },
    },

    cancelButton: {
        background: theme.palette.info.dark,

        '&:hover': {
            background: theme.palette.info.dark,
        },
    },

}));

interface ReviewPopupProps {
    isOpen: boolean;
    onClose: () => void;
    settlementId: number;
    settlementDetails:{ settlementAmount: number; caseName: string }
    onSuccess?: () => void;
}

const ReviewPopup = ({ isOpen, onClose, settlementId, settlementDetails, onSuccess }: ReviewPopupProps) => {
    const { classes } = useStyles();
    const { setAlert } = useSnackBar();
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    const handleSubmitForReview = () => {
        setIsFormSubmitting(true);
        const action = 'SUBMIT_FOR_REVIEW';
        const comment = 'Submitting settlement for review';
        ApproveSettlement(settlementId, action, comment)
            .then(() => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: 'Settlement submitted for review successfully',
                    type: 'success',
                }));
                onSuccess && onSuccess();
            })           
            .catch((error: AxiosError<ErrorData>) => {
                setIsFormSubmitting(false);
                setAlert((prev) => ({
                    ...prev,
                    message: error?.response?.data?.message || 'Failed to submit settlement for review',
                    type: 'error',
                }));
                onClose && onClose();
            });

    };


    return (
        <Dialog className={classes.dialogContainer} open={isOpen} onClose={onClose}>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                    position: 'absolute',
                    top: 32,
                    right: 32,
                    color: theme.palette.common.white,
                })}
            >
                <CloseIcon />
            </IconButton>

            <DialogTitle className={classes.dialogTitle}>
                Submit for Review
            </DialogTitle>

            <DialogContent sx={{ padding: '0px', overflow: 'hidden' }}>
                <DialogContentText className={classes.dialogContentText}>
                    This settlement will be sent for reviewer approval before payment can be released.
                </DialogContentText>
                <Box display="flex" flexDirection="row" justifyContent="space-between" pt={2}>
                    <DialogContentText className={classes.dialogContentText}>
                        Settlement
                    </DialogContentText>
                    <Typography className={classes.dialogContentValue}>
                        {settlementDetails.caseName}
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="row" justifyContent="space-between" pt={2}>
                    <DialogContentText className={classes.dialogContentText}>
                        Amount:
                    </DialogContentText>
                    <Typography className={classes.dialogContentValue}>
                        {numberToUSDCurrency(settlementDetails.settlementAmount)}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions className={classes.dialogActions} disableSpacing>
                <Button
                    className={clsx(classes.defaultButtonStyle, classes.submitButton)}
                    onClick={handleSubmitForReview}
                    disabled={isFormSubmitting}
                >
                    Submit for Review
                </Button>

                <Button
                    className={clsx(classes.defaultButtonStyle, classes.cancelButton)}
                    onClick={onClose}                   
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReviewPopup;